import { type ChangeEvent, type FormEvent, type ReactElement, useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import { toast } from 'sonner';

import {
  DATAS_DA_REGRA,
  TAMANHO_MAXIMO_BYTES,
} from '@/modulos/operacional/pre-credenciamento/contrato';
import {
  formatarTamanho,
  lerComoBase64,
  mensagemDoErro,
  novaChave,
  problemaDoArquivo,
} from '@/modulos/portal-credenciado/pre-credenciamento/dados';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Textarea } from '@/ui/base/textarea';
import { ResponsiveModal } from '@/ui/ResponsiveModal';

import type { EnvioDeDocumento, ItemDaApi } from './contrato';
import { useEnviarDocumento } from './dados';

/** Motivo sugerido no Vencido (Rafael, 30/09), editável. */
export const MOTIVO_DO_VENCIDO = 'Renovação de documento vencido';
const MOTIVO_MINIMO = 5;
const MOTIVO_MAXIMO = 1000;

/**
 * Envio e reenvio de um documento do credenciado.
 *
 * Diálogo próprio desta tela, separado do pré-credenciamento de propósito
 * (decisão F2 do plano de front): aquele já funciona e não é tocado, e este
 * tem o que aquele não tem — natureza, recurso e motivo no reenvio. Os
 * utilitários (arquivo em base64, tipo e tamanho, chave, mensagem da recusa) e
 * as datas por regra de validade são importados de lá, não copiados.
 *
 * Tipo e tamanho do arquivo e as datas exigidas são conferidos antes de
 * chamar a API; a regra de verdade (vencido, atestado antigo, situação) é do
 * banco, e a recusa chega com a frase pronta, mostrada aqui dentro. A chave de
 * idempotência nasce a cada abertura: clicar duas vezes não cria duas versões.
 */
export function DialogoDeEnvioDeDocumento({
  item,
  rotuloDoRecurso,
  aoFechar,
}: {
  /** O documento em envio; null fecha o diálogo. */
  item: ItemDaApi | null;
  /** Nome do cartão (placa, colaborador, empresa), para a pessoa saber onde está enviando. */
  rotuloDoRecurso: string;
  aoFechar: () => void;
}): ReactElement {
  const aberto = item !== null;
  const envio = useEnviarDocumento();

  const [chave, setChave] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [emitidoEm, setEmitidoEm] = useState('');
  const [validadeEm, setValidadeEm] = useState('');
  const [motivo, setMotivo] = useState('');
  const [problema, setProblema] = useState<string | null>(null);
  const [lendo, setLendo] = useState(false);

  const reenvio = item !== null && item.documento !== null;

  useEffect(() => {
    if (aberto) {
      setChave(novaChave());
      setArquivo(null);
      setEmitidoEm('');
      setValidadeEm('');
      setMotivo(item?.situacao === 'VENCIDO' ? MOTIVO_DO_VENCIDO : '');
      setProblema(null);
      envio.reset();
    }
    // `envio.reset` muda a cada render; o que importa é a abertura.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, item?.recursoId, item?.requisito.codigo]);

  const enviando = lendo || envio.isPending;
  const regra = item ? DATAS_DA_REGRA[item.requisito.regraValidade] : null;

  function fechar() {
    if (!enviando) aoFechar();
  }

  function escolher(evento: ChangeEvent<HTMLInputElement>) {
    const escolhido = evento.target.files?.[0] ?? null;
    setArquivo(escolhido);
    setProblema(escolhido ? problemaDoArquivo(escolhido) : null);
  }

  function problemaDoFormulario(): string | null {
    if (!item || !regra) return null;
    if (regra.validade === 'obrigatoria' && !validadeEm) {
      return 'Informe a data de validade do documento.';
    }
    if (regra.emissao === 'obrigatoria' && !emitidoEm) {
      return 'Informe a data de emissão do documento.';
    }
    if (item.requisito.regraValidade === 'CERTIDAO_180_DIAS' && !emitidoEm && !validadeEm) {
      return 'Informe a validade da certidão ou, se ela não tiver, a data de emissão.';
    }
    if (emitidoEm && validadeEm && validadeEm < emitidoEm) {
      return 'A data de validade não pode ser anterior à de emissão.';
    }
    if (reenvio && motivo.trim().length < MOTIVO_MINIMO) {
      return `Escreva o motivo do reenvio, com pelo menos ${MOTIVO_MINIMO} caracteres.`;
    }
    return null;
  }

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!item || enviando) return;
    if (!arquivo) {
      setProblema('Escolha o arquivo do documento.');
      return;
    }
    const doArquivo = problemaDoArquivo(arquivo);
    if (doArquivo) {
      setProblema(doArquivo);
      return;
    }
    const doFormulario = problemaDoFormulario();
    if (doFormulario) {
      setProblema(doFormulario);
      return;
    }
    setProblema(null);

    let conteudoBase64: string;
    setLendo(true);
    try {
      conteudoBase64 = await lerComoBase64(arquivo);
    } catch {
      setProblema('Não foi possível ler o arquivo. Escolha-o de novo.');
      return;
    } finally {
      setLendo(false);
    }

    const corpo: EnvioDeDocumento = {
      natureza: item.natureza,
      ...(item.natureza === 'EMPRESA' ? {} : { recursoId: item.recursoId }),
      requisito: item.requisito.codigo,
      arquivoNome: arquivo.name,
      conteudoBase64,
      chaveIdempotencia: chave,
      ...(emitidoEm ? { emitidoEm } : {}),
      ...(validadeEm ? { validadeEm } : {}),
      ...(reenvio ? { motivo: motivo.trim() } : {}),
    };
    try {
      await envio.mutateAsync(corpo);
      toast.success('Documento enviado', {
        description: 'O DTP vai analisar e você acompanha a situação nesta tela.',
      });
      aoFechar();
    } catch {
      // A mensagem da recusa aparece dentro do diálogo (envio.error).
    }
  }

  const idForm = 'form-envio-documento-credenciado';
  const mensagem =
    problema ??
    (envio.isError
      ? mensagemDoErro(envio.error, 'Não foi possível enviar o documento. Tente de novo.')
      : null);

  return (
    <ResponsiveModal
      open={aberto}
      onOpenChange={(v) => !v && fechar()}
      title={reenvio ? 'Enviar nova versão' : 'Enviar documento'}
      description={item ? `${item.requisito.titulo} — ${rotuloDoRecurso}` : undefined}
      desktopMaxWidth="max-w-lg"
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={fechar} disabled={enviando}>
            Cancelar
          </Button>
          <Button type="submit" form={idForm} disabled={enviando}>
            {enviando ? 'Enviando…' : 'Enviar'}
          </Button>
        </div>
      }
    >
      {item && regra && (
        <form id={idForm} noValidate onSubmit={enviar} className="space-y-4 text-sm">
          {mensagem && (
            <Alert variant="destructive">
              <AlertDescription>{mensagem}</AlertDescription>
            </Alert>
          )}

          {reenvio && (
            <p className="text-muted-foreground">
              A versão atual fica guardada no histórico. A nova vai para análise do DTP.
            </p>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="arquivo-documento-credenciado">Arquivo do documento</Label>
            <Input
              id="arquivo-documento-credenciado"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={escolher}
              disabled={enviando}
              aria-describedby="arquivo-documento-credenciado-dica"
              className="cursor-pointer"
            />
            <p id="arquivo-documento-credenciado-dica" className="text-muted-foreground text-xs">
              PDF, JPG ou PNG, até {formatarTamanho(TAMANHO_MAXIMO_BYTES)}.
              {arquivo && !problemaDoArquivo(arquivo)
                ? ` Escolhido: ${arquivo.name} (${formatarTamanho(arquivo.size)}).`
                : ''}
            </p>
          </div>

          <p className="bg-muted flex items-start gap-2 rounded-lg p-3">
            <Info className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{regra.dica}</span>
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            {regra.emissao !== 'nao' && (
              <div className="space-y-1.5">
                <Label htmlFor="data-emissao-credenciado">
                  Data de emissão{regra.emissao === 'opcional' ? ' (opcional)' : ''}
                </Label>
                <Input
                  id="data-emissao-credenciado"
                  type="date"
                  value={emitidoEm}
                  onChange={(e) => setEmitidoEm(e.target.value)}
                  disabled={enviando}
                  required={regra.emissao === 'obrigatoria'}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="data-validade-credenciado">
                Data de validade{regra.validade === 'opcional' ? ' (opcional)' : ''}
              </Label>
              <Input
                id="data-validade-credenciado"
                type="date"
                value={validadeEm}
                onChange={(e) => setValidadeEm(e.target.value)}
                disabled={enviando}
                required={regra.validade === 'obrigatoria'}
              />
            </div>
          </div>

          {reenvio && (
            <div className="space-y-1.5">
              <Label htmlFor="motivo-reenvio">Motivo do reenvio</Label>
              <Textarea
                id="motivo-reenvio"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                maxLength={MOTIVO_MAXIMO}
                rows={2}
                disabled={enviando}
                required
                aria-describedby="motivo-reenvio-dica"
              />
              <p id="motivo-reenvio-dica" className="text-muted-foreground text-xs">
                Conte em poucas palavras o que mudou nesta versão. Fica registrado no histórico.
              </p>
            </div>
          )}
        </form>
      )}
    </ResponsiveModal>
  );
}
